// ─────────────────────────────────────────────
//  LinkedSliders.jsx — Presupuesto ↔ Peso acoplados
// ─────────────────────────────────────────────

import React from 'react';
import styles from './LinkedSliders.module.css';
import { formatCurrency } from '../../utils/format';

function formatWeightDisplay(grams) {
	const g = Math.round(grams);
	if (g >= 1000) {
		const kg = g / 1000;
		return kg >= 10 ? `${kg.toFixed(1)} kg` : `${kg.toFixed(2)} kg`;
	}
	return `${g} g`;
}

function SliderBlock({
	label,
	displayValue,
	subLabel,
	min,
	max,
	step,
	value,
	onChange,
	disabled,
	accent,
	ariaLabel,
	ariaValueText,
	axisFormat,
	quickOptions = [],
}) {
	const fillPct = ((value - min) / (max - min)) * 100;
	const formatAxis = axisFormat === 'currency' ? formatCurrency : formatWeightDisplay;
	const accentClass = accent === 'turquoise' ? styles.accentTurquoise : styles.accentMagenta;

	const isOptionActive = (optionValue) =>
		axisFormat === 'currency'
			? value === optionValue
			: Math.round(value) === optionValue;

	return (
		<div className={`${styles.sliderBlock} ${disabled ? styles.disabled : ''}`}>
			<div className={styles.sliderHeader}>
				<div>
					<p className={`${styles.sliderLabel} ${accentClass}`}>{label}</p>
					<p className={`${styles.sliderValue} ${accentClass}`} aria-live="polite">
						{displayValue}
					</p>
				</div>
				{subLabel && (
					<p className={`${styles.sliderSub} ${accentClass}`}>{subLabel}</p>
				)}
			</div>

			<div className={styles.trackWrap}>
				<div className={styles.trackBg}>
					<div
						className={`${styles.trackFill} ${accentClass}`}
						style={{ width: `${fillPct}%` }}
					/>
				</div>
				<input
					type="range"
					className={`${styles.slider} ${accentClass}`}
					min={min}
					max={max}
					step={step}
					value={value}
					disabled={disabled}
					aria-label={ariaLabel}
					aria-valuemin={min}
					aria-valuemax={max}
					aria-valuenow={value}
					aria-valuetext={ariaValueText}
					onChange={(e) => onChange(Number(e.target.value))}
				/>
			</div>

			<div className={styles.axisLabels} aria-hidden="true">
				<span>{formatAxis(min)}</span>
				<span>{formatAxis(max)}</span>
			</div>

			{quickOptions.length > 0 && (
				<div
					className={styles.quickBtns}
					role="group"
					aria-label={`Opciones rápidas: ${label}`}
				>
					{quickOptions.map((opt) => {
						const isActive = isOptionActive(opt.value);
						return (
							<button
								key={opt.id ?? opt.value}
								type="button"
								className={`${styles.quickBtn} ${accentClass} ${isActive ? styles.quickBtnActive : ''}`}
								onClick={() => onChange(opt.value)}
								disabled={disabled}
								aria-pressed={isActive}
								aria-label={opt.label}
							>
								{opt.label}
							</button>
						);
					})}
				</div>
			)}
		</div>
	);
}

export default function LinkedSliders({
	budget,
	weightGrams,
	onBudgetChange,
	onWeightChange,
	budgetMin,
	budgetMax,
	weightMin,
	weightMax,
	disabled,
	budgetQuickOptions = [],
	weightQuickOptions = [],
}) {
	const roundedBudget = Math.round(budget);
	const roundedWeight = Math.round(weightGrams);

	return (
		<section className={styles.card} aria-label="Controles de presupuesto y peso">

			<SliderBlock
				label="Presupuesto"
				displayValue={formatCurrency(roundedBudget, { forceInteger: true })}
				subLabel="Quiero gastar"
				min={budgetMin}
				max={budgetMax}
				step={1}
				value={roundedBudget}
				onChange={onBudgetChange}
				disabled={disabled}
				accent="turquoise"
				ariaLabel="Presupuesto en pesos"
				ariaValueText={`${formatCurrency(roundedBudget, { forceInteger: true })} de presupuesto`}
				axisFormat="currency"
				quickOptions={budgetQuickOptions}
			/>

			<div className={styles.linkIcon} aria-hidden="true">
				<span className={styles.linkArrow}>
					<svg
						className={styles.linkArrowSvg}
						viewBox="0 0 24 24"
						fill="none"
						xmlns="http://www.w3.org/2000/svg"
						aria-hidden="true"
					>
						<path
							d="M12 5v14"
							stroke="currentColor"
							strokeWidth="1.75"
							strokeLinecap="round"
						/>
						<path
							d="M8 9l4-4 4 4"
							stroke="currentColor"
							strokeWidth="1.75"
							strokeLinecap="round"
							strokeLinejoin="round"
						/>
						<path
							d="M8 15l4 4 4-4"
							stroke="currentColor"
							strokeWidth="1.75"
							strokeLinecap="round"
							strokeLinejoin="round"
						/>
					</svg>
				</span>
			</div>

			<SliderBlock
				label="Peso"
				displayValue={formatWeightDisplay(roundedWeight)}
				subLabel="Quiero llevar"
				min={weightMin}
				max={weightMax}
				step={1}
				value={roundedWeight}
				onChange={onWeightChange}
				disabled={disabled}
				accent="magenta"
				ariaLabel="Peso en gramos"
				ariaValueText={`${formatWeightDisplay(roundedWeight)} de peso`}
				axisFormat="weight"
				quickOptions={weightQuickOptions}
			/>

			{disabled && (
				<p className={styles.emptyHint} role="status">
					Ingresa el precio de referencia para activar los controles
				</p>
			)}
		</section>
	);
}
